import type { ElementType } from "react";
import { CircleUserRound, LogOut, Settings } from "lucide-react";
import { useAuth } from "react-oidc-context";
import { useSettingsModal } from "@/contexts/SettingsModalContext";
import { cn } from "@/lib/utils";
import { getDisplayName, getProfileRole } from "@/lib/userDisplay";
import { forgetSession } from "@/auth/session";
import { createSignoutArgs } from "@/auth/oidcConfig";

interface Props {
  onClose: () => void;
  onOpenSettings: () => void;
}

export function UserPanelContent({ onClose, onOpenSettings }: Props) {
  const auth = useAuth();

  const profile = auth.user?.profile;
  const name = getDisplayName(profile);
  const email = profile?.email ?? "";
  const role = getProfileRole(profile);
  const { openProfileModal } = useSettingsModal();

  const openSettings = () => {
    onClose();
    onOpenSettings();
  };

  const signOut = () => {
    onClose();
    // An explicit sign-out: this browser must not sign in again by itself.
    forgetSession();

    void auth.signoutRedirect(createSignoutArgs(auth.settings, window.location.origin));
  };

  return (
    <div
      className={cn(
        "w-[min(calc(100vw-32px),292px)] overflow-hidden rounded-2xl",
        "bg-surface text-sm text-secondary",
        "shadow-2xl shadow-black/20 ring-1 ring-border/35",
        "backdrop-blur-xl",
      )}
    >
      {/* User identity */}
      <div className="px-4 pb-3.5 pt-4">
        <div className="min-w-0">
          <div className="truncate text-[15px] font-semibold leading-6 text-primary">{name}</div>

          {email && <div className="mt-1 truncate text-xs leading-4 text-muted">{email}</div>}
        </div>

        {role && (
          <div className="mt-3 inline-flex rounded-full bg-brand-cyan/10 px-2.5 py-1 text-2xs font-semibold uppercase tracking-widest text-brand-cyan">
            {role}
          </div>
        )}
      </div>

      <div className="mx-3 h-px bg-border/35" />

      {/* Menu */}
      <div className="p-2">
        <MenuItem
          icon={CircleUserRound}
          label="Profile"
          onClick={() => {
            onClose();
            openProfileModal();
          }}
        />
        <MenuItem icon={Settings} label="Settings" onClick={openSettings} />
      </div>

      <div className="mx-3 h-px bg-border/35" />

      {/* Sign out */}
      <div className="p-2">
        <MenuItem icon={LogOut} label="Sign out" onClick={signOut} variant="danger" />
      </div>
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
  variant = "default",
}: {
  icon: ElementType;
  label: string;
  onClick: () => void;
  variant?: "default" | "danger";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl px-3 py-2.5",
        "text-left text-sm font-medium transition-colors cursor-pointer",
        "focus:outline-none focus:ring-2 focus:ring-brand-cyan/25",
        variant === "danger"
          ? "text-secondary hover:bg-status-danger/10 hover:text-status-danger"
          : "text-secondary hover:bg-surface-elevated hover:text-primary",
      )}
    >
      <Icon
        className={cn("h-4 w-4 shrink-0", variant === "danger" ? "text-current" : "text-muted")}
        strokeWidth={1.8}
      />

      <span className="truncate">{label}</span>
    </button>
  );
}
